import { ZONE_RADIUS_METERS, type PlaceKind, type PlaceMatch } from '@chukua/common';

/** A business or an address comes back as a bare point: a site's gate, yard and parking fit inside these. */
const POINT_RADIUS_METERS: Record<PlaceKind, number> = { business: 200, address: 100, area: ZONE_RADIUS_METERS.default };

function metersBetween([lng1, lat1]: [number, number], [lng2, lat2]: [number, number]): number {
  const toRad = (degrees: number) => (degrees * Math.PI) / 180;
  const a = Math.sin(toRad(lat2 - lat1) / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(toRad(lng2 - lng1) / 2) ** 2;
  return 2 * 6_371_008.8 * Math.asin(Math.sqrt(a));
}

/**
 * The radius a zone started from a place gets: enough to cover the place's extent (half its bounding box's
 * diagonal, so a town's circle reaches its edges), or a site-sized default when it has none. Rounded to 50 m
 * and kept within what a zone allows; the admin tunes it on the map.
 */
export function radiusForPlace({ kind, bbox }: Pick<PlaceMatch, 'kind' | 'bbox'>): number {
  const halfDiagonal = bbox ? metersBetween([bbox[0], bbox[1]], [bbox[2], bbox[3]]) / 2 : 0;
  const radius = halfDiagonal > 0 ? Math.round(halfDiagonal / 50) * 50 : POINT_RADIUS_METERS[kind];
  return Math.min(ZONE_RADIUS_METERS.max, Math.max(ZONE_RADIUS_METERS.min, radius));
}
